import { ExerciseDefinition, ExerciseType } from "./types";
import {
  generateChord,
  chordIntervals,
  identifyInterval,
  generateMajorScale,
} from "./musicTheory";
import MidiNumbers from "components/piano/MidiNumbers";

export interface MusicExerciseGeneratorOptions {
  keyRoot: number; // MIDI number for the root of the key, e.g., C4 as 60
  difficulty: number; // Difficulty level
  sessionLength: number; // in minutes or number of exercises
}

export class MusicExerciseGenerator {
  private scale: number[];
  private chordTypes: string[];
  private noteNamesInKey: string[];

  // Piano range constants
  private firstNote = MidiNumbers.fromNote("c3");
  private lastNote = MidiNumbers.fromNote("b5");

  constructor(
    private options: MusicExerciseGeneratorOptions,
    private moduleType: string
  ) {
    // First, generate a single-octave scale
    const singleOctaveScale = generateMajorScale(options.keyRoot);

    // Based on difficulty, decide if we need an extended scale
    if (options.difficulty === 1) {
      // Just use the single octave scale
      this.scale = singleOctaveScale;
    } else {
      // Difficulty 2 or 3: Extend the scale over multiple octaves
      this.scale = this.generateExtendedScale(singleOctaveScale);
    }

    this.chordTypes = Object.keys(chordIntervals);
    this.initializeChordTypes();
    this.noteNamesInKey = this.scale.map((midi) =>
      MidiNumbers.getNoteName(midi)
    );
  }

  private initializeChordTypes(): void {
    const allChordTypes = {
      basic: ["major", "minor", "diminished", "augmented"],
      intermediate: ["dominant_seventh", "major_seventh", "minor_seventh"],
      advanced: [
        "minor_major_seventh",
        "augmented_major_seventh",
        "half_diminished_seventh",
        "diminished_seventh",
        "dominant_seventh_flat_five",
      ],
    };

    // Choose chord types based on difficulty
    if (this.options.difficulty === 1) {
      this.chordTypes = allChordTypes.basic;
    } else if (this.options.difficulty === 2) {
      this.chordTypes = [...allChordTypes.basic, ...allChordTypes.intermediate];
    } else if (this.options.difficulty === 3) {
      this.chordTypes = Object.keys(chordIntervals);
    }
  }

  generateSessionExercises(): ExerciseDefinition[] {
    const exercises: ExerciseDefinition[] = [];

    let exerciseTypes = [
      { type: this.generateNoteIdExercise, weight: 1.5 },
      { type: this.generateIntervalExercise, weight: 3 },
      { type: this.generateDiatonicChordExercise, weight: 2 },
      { type: this.generateScaleAscendingDescendingExercise, weight: 2 },
    ];

    // Apply moduleType filtering
    if (this.moduleType === "Scale Mastery") {
      // Create Scale Ascending/Descending Exercise
      // Only generate scale-related exercises, always start with Scale Ascending/Descending
      exerciseTypes = [
        { type: this.generateScaleAscendingDescendingExercise, weight: 3 },
      ];
    } else if (this.moduleType === "Interval Recognition") {
      // Only generate interval-related exercises
      exerciseTypes = [{ type: this.generateIntervalExercise, weight: 3 }];
    } else if (this.moduleType === "Chord Training") {
      // Only generate chord-related exercises
      exerciseTypes = [{ type: this.generateDiatonicChordExercise, weight: 3 }];
    }

    // Adjust weights based on difficulty
    if (this.options.difficulty === 1) {
      exerciseTypes.forEach((e) => (e.weight *= 0.5)); // Easier sessions have lower weight
    } else if (this.options.difficulty === 3) {
      exerciseTypes.forEach((e) => (e.weight *= 1.5)); // Harder sessions have higher weight
    }

    // Calculate total weight
    const totalWeight = exerciseTypes.reduce(
      (acc, curr) => acc + curr.weight,
      0
    );
    const numberOfExercises = Math.ceil(this.options.sessionLength); // Assuming each exercise might take about 5 minutes on average

    // Generate exercises based on weights
    for (let i = 0; i < numberOfExercises; i++) {
      const randomIndex = this.weightedRandomIndex(exerciseTypes, totalWeight);
      const exerciseGenerator = exerciseTypes[randomIndex].type;
      exercises.push(exerciseGenerator.call(this));
    }

    return exercises;
  }

  private generateNoteIdExercise(): ExerciseDefinition {
    const randomNote =
      this.scale[Math.floor(Math.random() * this.scale.length)];
    const correctNoteName = MidiNumbers.getNoteName(randomNote);

    // Determine UI mode
    let uiMode: "PIANO" | "MULTIPLE_CHOICE" = this.determineUIMode(
      this.options.difficulty
    );

    let data: any = { notes: [randomNote], uiMode };

    if (uiMode === "MULTIPLE_CHOICE") {
      // Decide whether to quiz by note name or by scale degree
      const useNumberSystem = Math.random() < 0.5; // 50% chance

      if (useNumberSystem) {
        // Identify the scale degree of this note
        const scaleDegree = this.getRomanScaleDegree(randomNote); // Get the scale degree of the chord
        const correctAnswer = scaleDegree; // We return Roman numerals now, not numbers
        const numberSystemChoices = ["I", "ii", "iii", "IV", "V", "vi", "vii°"];

        // Filter choices so we don't offer duplicates
        const choices = this.getMultipleChoiceOptions(
          {
            label: correctAnswer,
            midiNumbers: this.getScaleDegreeMidiNumbers(scaleDegree),
          }, // Use midiNumbers for the correct answer
          numberSystemChoices.map((n) => ({
            label: n,
            midiNumbers: this.getScaleDegreeMidiNumbers(n),
          })),
          4
        );

        data.promptText = "Which scale degree is this note?";
        data.choices = choices;
        data.correctAnswer = correctAnswer;
      } else {
        // Use note names
        const choices = this.getMultipleChoiceOptions(
          { label: correctNoteName, midiNumbers: [randomNote] },
          this.noteNamesInKey.map((n) => ({
            label: n,
            midiNumbers: [this.scale[this.noteNamesInKey.indexOf(n)]],
          })),
          4
        );
        data.promptText = "Which note in the scale is this?";
        data.choices = choices;
        data.correctAnswer = correctNoteName;
      }
    }

    return {
      id: `note_id_${Date.now()}`,
      type: ExerciseType.NOTE_ID,
      keyContext: MidiNumbers.getNoteName(this.options.keyRoot),
      difficulty: this.options.difficulty,
      data,
    };
  }

  private generateIntervalExercise(): ExerciseDefinition {
    // Ensure intervals are generated within a reasonable musical range
    const maxIntervalSize = 12; // Max size of interval in semitones, could be up to an octave
    const note1 = this.scale[Math.floor(Math.random() * this.scale.length)];
    let note2 = note1 + Math.floor(Math.random() * maxIntervalSize + 1); // Ensures intervals up to an octave

    // Check and adjust note2 if it exceeds the scale range
    if (!this.scale.includes(note2)) {
      note2 = note1 - Math.floor(Math.random() * maxIntervalSize + 1);
      if (note2 < this.firstNote) {
        note2 = note1; // fallback to unison if adjustment is not possible within scale
      }
    }

    const intervalDescription = identifyInterval(note1, note2);
    let uiMode: "PIANO" | "MULTIPLE_CHOICE" = this.determineUIMode(
      this.options.difficulty
    );

    let data: any = { notes: [note1, note2], uiMode };

    if (uiMode === "MULTIPLE_CHOICE") {
      const commonIntervals = [
        { label: "Major Second", semitones: 2 },
        { label: "Major Third", semitones: 4 },
        { label: "Perfect Fifth", semitones: 7 },
        { label: "Minor Third", semitones: 3 },
        { label: "Perfect Fourth", semitones: 5 },
      ];

      // Convert them to actual objects with midiNumbers
      const intervalPool = commonIntervals.map((intervalObj) => {
        const intervalNote = note1 + intervalObj.semitones;
        return {
          label: intervalObj.label,
          midiNumbers: [note1, intervalNote],
        };
      });

      const correctInterval = intervalDescription || "Unknown Interval";
      // Find a matching entry or create a fallback
      let correctIntervalObj = intervalPool.find(
        (i) => i.label === correctInterval
      );
      if (!correctIntervalObj) {
        // If not found, just create one from the identified semitones (if applicable)
        // For simplicity, assume you've identified the semitones from note1 to note2
        const semitones = Math.abs(note2 - note1);
        correctIntervalObj = {
          label: correctInterval,
          midiNumbers: [note1, note2],
        };
        intervalPool.push(correctIntervalObj);
      }

      const choices = this.getMultipleChoiceOptions(
        correctIntervalObj,
        intervalPool,
        4
      );
      data.promptText = "What interval did you hear?";
      data.choices = choices;
      data.correctAnswer = correctInterval;
    }

    return {
      id: `interval_id_${Date.now()}`,
      type: ExerciseType.INTERVAL_ID,
      keyContext: MidiNumbers.getNoteName(this.options.keyRoot),
      difficulty: this.options.difficulty,
      data,
    };
  }

  private generateChordExercise(): ExerciseDefinition {
    const allChordTypes = {
      basic: ["major", "minor", "diminished", "augmented"],
      intermediate: ["dominant_seventh", "major_seventh", "minor_seventh"],
      advanced: [
        "minor_major_seventh",
        "augmented_major_seventh",
        "half_diminished_seventh",
        "diminished_seventh",
        "dominant_seventh_flat_five",
      ],
    };

    // Combine all chord types and create a pool with midiNumbers
    const fullChordPool = [
      ...allChordTypes.basic,
      ...allChordTypes.intermediate,
      ...allChordTypes.advanced,
    ].map((ct) => {
      const chordObj = generateChord(this.options.keyRoot, ct);
      return {
        label: this.formatChordTypeName(ct),
        midiNumbers: chordObj.notes,
      };
    });

    const randomChordType =
      this.chordTypes[Math.floor(Math.random() * this.chordTypes.length)];
    const chord = generateChord(this.options.keyRoot, randomChordType);

    let uiMode: "PIANO" | "MULTIPLE_CHOICE" = this.determineUIMode(
      this.options.difficulty
    );

    let data: any = { notes: chord.notes, uiMode };

    if (uiMode === "MULTIPLE_CHOICE") {
      // Convert randomChordType to a user-friendly chord name
      const displayChordName = this.formatChordTypeName(randomChordType);
      const correctChoice = {
        label: displayChordName,
        midiNumbers: chord.notes,
      };

      // Use the full pool of chord names to find distractors
      const choices = this.getMultipleChoiceOptions(
        correctChoice,
        fullChordPool,
        4
      );
      data.promptText = "What chord is this?";
      data.choices = choices;
      data.correctAnswer = displayChordName;
    }

    return {
      id: `chord_id_${Date.now()}`,
      type: ExerciseType.CHORD_ID,
      keyContext: MidiNumbers.getNoteName(this.options.keyRoot),
      difficulty: this.options.difficulty,
      data,
    };
  }

  private generateDiatonicChordExercise(): ExerciseDefinition {
    // Define diatonic chords for the given key
    const diatonicChords = this.getDiatonicChords(this.options.keyRoot);

    // Select a random chord from the diatonic chords
    const randomChord =
      diatonicChords[Math.floor(Math.random() * diatonicChords.length)];
    const chordNotes = randomChord.notes;
    const chordLabel = randomChord.label;
    const chordRoot = MidiNumbers.getNoteName(chordNotes[0]); // Get root note for the label, assuming the first note in chord is the root

    // Determine whether we ask about the chord or the scale degree
    const useNumberSystem = Math.random() < 0.5; // 50% chance for number system (scale degree question)

    // Introduce inversions on harder levels (e.g., difficulty level 2 and above)
    let chordVariants = [chordNotes]; // Start with the root position

    if (this.options.difficulty > 1) {
      // Generate inversions
      chordVariants.push(this.getChordInversion(chordNotes, 1)); // First inversion
      chordVariants.push(this.getChordInversion(chordNotes, 2)); // Second inversion
    }

    let uiMode: "PIANO" | "MULTIPLE_CHOICE" = this.determineUIMode(
      this.options.difficulty
    );

    let data: any = { notes: chordNotes, uiMode };

    if (uiMode === "MULTIPLE_CHOICE") {
      if (useNumberSystem) {
        // We are asking about the scale degree
        const scaleDegree = this.getRomanScaleDegree(chordNotes[0]); // Get the scale degree of the chord
        const correctAnswer = scaleDegree; // We return Roman numerals now, not numbers
        const numberSystemChoices = ["I", "ii", "iii", "IV", "V", "vi", "vii°"];

        // Filter choices so we don't offer duplicates
        const choices = this.getMultipleChoiceOptions(
          { label: correctAnswer, midiNumbers: chordNotes },
          numberSystemChoices.map((n) => ({
            label: n,
            midiNumbers: this.getScaleDegreeMidiNumbers(n),
          })),
          4
        );

        data.promptText = "What scale degree is this chord?";
        data.choices = choices;
        data.correctAnswer = correctAnswer;
      } else {
        // Create multiple choice options with label and midiNumbers (including inversions)
        const choices = this.getMultipleChoiceOptions(
          {
            label: `${chordRoot} ${chordLabel}`,
            midiNumbers: chordVariants[0],
          },
          diatonicChords.map((chord) => ({
            label: `${MidiNumbers.getNoteName(chord.notes[0])} ${chord.label}`,
            midiNumbers: chord.notes,
          })),
          4
        );

        data.promptText = "What diatonic chord is this?";
        data.choices = choices;
        data.correctAnswer = `${chordRoot} ${chordLabel}`;
      }
    }

    return {
      id: `diatonic_chord_id_${Date.now()}`,
      type: ExerciseType.CHORD_ID,
      keyContext: MidiNumbers.getNoteName(this.options.keyRoot),
      difficulty: this.options.difficulty,
      data,
    };
  }

  private lastGeneratedNotes: number[] | null = null; // Store the last generated notes

  private generateScaleAscendingDescendingExercise(): ExerciseDefinition {
    const scaleNotes = this.scale;

    // Decide if the exercise will be ascending or descending
    const direction = Math.random() < 0.5 ? "ascending" : "descending";

    // Decide if we use a fragment or the full scale
    const useFragment = Math.random() < 0.5; // 50% chance for a fragment
    let notes = scaleNotes;

    if (useFragment) {
      // Select a random fragment (start and end index)
      const startIdx = Math.floor(Math.random() * (scaleNotes.length - 5)); // Ensure enough notes for a fragment
      const endIdx = startIdx + Math.floor(Math.random() * 5) + 3; // Ensure fragment length is at least 3 notes
      notes = scaleNotes.slice(startIdx, endIdx); // Slice the fragment from the scale
    }

    // If descending, reverse the order of the notes
    if (direction === "descending") {
      notes = notes.reverse();
    }

    // Check if the new notes are the same as the previous ones
    if (
      this.lastGeneratedNotes &&
      this.areArraysEqual(this.lastGeneratedNotes, notes)
    ) {
      // If they are the same, regenerate the notes
      return this.generateScaleAscendingDescendingExercise();
    }

    // Update the last generated notes
    this.lastGeneratedNotes = notes;

    const data = {
      notes,
    };

    const uniqueId = `scale_ascending_descending_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    return {
      id: uniqueId,
      type: ExerciseType.SCALE_ASCENDING_DESCENDING,
      keyContext: MidiNumbers.getNoteName(this.options.keyRoot),
      difficulty: this.options.difficulty,
      data,
    };
  }

  private weightedRandomIndex(
    types: { type: Function; weight: number }[],
    totalWeight: number
  ): number {
    let random = Math.random() * totalWeight;
    for (let i = 0; i < types.length; i++) {
      if (random < types[i].weight) {
        return i;
      }
      random -= types[i].weight;
    }
    return types.length - 1;
  }

  // Instead of returning string[], return an array of objects with label and midiNumbers
  private getMultipleChoiceOptions<
    T extends { label: string; midiNumbers?: number[] }
  >(correct: T, pool: T[], totalChoices: number): T[] {
    const filtered = pool.filter((p) => p.label !== correct.label);
    const shuffled = filtered.sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, totalChoices - 1);
    selected.push(correct);
    return this.shuffleArray(selected);
  }

  private shuffleArray<T>(array: T[]): T[] {
    return array.sort(() => Math.random() - 0.5);
  }

  private formatChordTypeName(type: string): string {
    // Replace all underscores with spaces and capitalize the first letter of each word
    const formatted = type.replace(/_/g, " "); // Using regex to replace all instances of "_"
    return formatted
      .split(" ") // Split the string into words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
      .join(" "); // Join the words back into a single string
  }

  private determineUIMode(difficulty: number): "PIANO" | "MULTIPLE_CHOICE" {
    // Increase likelihood of multiple choice for easier levels
    const randomChance = Math.random();
    switch (difficulty) {
      case 1:
        return randomChance < 0.5 ? "MULTIPLE_CHOICE" : "PIANO"; // 50% chance for multiple choice
      case 2:
        return randomChance < 0.3 ? "MULTIPLE_CHOICE" : "PIANO"; // 30% chance for multiple choice
      case 3:
        return randomChance < 0.3 ? "MULTIPLE_CHOICE" : "PIANO"; // 30% chance for multiple choice
      default:
        return "PIANO";
    }
  }

  private getScaleDegree(note: number): number {
    // scale is an array of MIDI notes for the major scale starting from keyRoot
    // The tonic (keyRoot) is scale[0].
    const degree = this.scale.indexOf(note) + 1; // 1-based indexing
    return degree;
  }

  // Helper function to get the scale degree as a Roman numeral
  private getRomanScaleDegree(note: number): string {
    // Get the degree as an index (1-based)
    const degree = this.scale.indexOf(note) + 1; // 1-based indexing

    // Convert to Roman numeral
    switch (degree) {
      case 1:
        return "I";
      case 2:
        return "ii";
      case 3:
        return "iii";
      case 4:
        return "IV";
      case 5:
        return "V";
      case 6:
        return "vi";
      case 7:
        return "vii°";
      default:
        return "";
    }
  }

  /**
   * Generate an extended scale covering multiple octaves within the range of C3 to B5.
   * We'll replicate the scale notes across octaves until we fill the range.
   */
  private generateExtendedScale(singleOctaveScale: number[]): number[] {
    const extendedScale: number[] = [];
    // The major scale repeats every 12 semitones (one octave)
    // We can generate several octaves of this scale and then filter by the desired range.
    const octaveSpan = 12; // 12 semitones per octave

    // Find the lowest octave for the root key that doesn't go below firstNote
    // We'll start from the firstNote and go up, adding notes that match the scale degrees
    const rootNote = singleOctaveScale[0];
    // We'll generate from firstNote to lastNote:
    for (let midi = this.firstNote; midi <= this.lastNote; midi++) {
      // Check if midi corresponds to a scale degree
      // We know singleOctaveScale defines the pattern of notes for one octave starting at rootNote
      // To check if a note belongs to the scale, we can compare (midi - rootNote) mod 12
      // If (midi - rootNote) % 12 matches the intervals found in singleOctaveScale relative to rootNote,
      // then midi is in the scale.
      const semitoneDistance = (midi - rootNote) % 12;
      if (semitoneDistance < 0) continue; // negative if we are below the rootNote by some octaves
      // Extract the pattern of intervals from singleOctaveScale
      const intervalsFromRoot = singleOctaveScale.map((n) => n - rootNote);
      // If intervalsFromRoot includes semitoneDistance, then midi is a scale note
      if (intervalsFromRoot.includes(semitoneDistance)) {
        extendedScale.push(midi);
      }
    }

    return extendedScale;
  }

  // Helper function to get diatonic chords in the key
  private getDiatonicChords(keyRoot: number) {
    const scale = generateMajorScale(keyRoot); // Using the major scale for now
    const diatonicChords = [];

    if (this.options.difficulty === 1) {
      // Level 1: Only triads (major, minor, diminished)
      diatonicChords.push(
        this.buildChord(scale[0], "major"), // I
        this.buildChord(scale[1], "minor"), // ii
        this.buildChord(scale[2], "minor"), // iii
        this.buildChord(scale[3], "major"), // IV
        this.buildChord(scale[4], "major"), // V
        this.buildChord(scale[5], "minor"), // vi
        this.buildChord(scale[6], "diminished") // vii°
      );
    } else if (this.options.difficulty === 2) {
      // Level 2: Add seventh chords (major 7th, minor 7th, dominant 7th)
      diatonicChords.push(
        this.buildChord(scale[0], "major"), // I
        this.buildChord(scale[1], "minor"), // ii
        this.buildChord(scale[2], "minor"), // iii
        this.buildChord(scale[3], "major"), // IV
        this.buildChord(scale[4], "dominant_seventh"), // V7
        this.buildChord(scale[5], "minor"), // vi
        this.buildChord(scale[6], "diminished") // vii°
      );
    } else if (this.options.difficulty === 3) {
      // Level 3: Add more complex seventh chords and extended chords
      diatonicChords.push(
        this.buildChord(scale[0], "major"), // I
        this.buildChord(scale[1], "minor"), // ii
        this.buildChord(scale[2], "minor"), // iii
        this.buildChord(scale[3], "major"), // IV
        this.buildChord(scale[4], "dominant_seventh"), // V7
        this.buildChord(scale[5], "minor"), // vi
        this.buildChord(scale[6], "diminished"), // vii°
        this.buildChord(scale[0], "major_seventh"), // I7
        this.buildChord(scale[4], "dominant_seventh_flat_five") // V7b5
      );
    }

    return diatonicChords;
  }

  // Helper function to build a chord given a root and chord type
  private buildChord(root: number, chordType: string) {
    const chord = generateChord(root, chordType);
    return {
      label: this.formatChordTypeName(chordType),
      notes: chord.notes,
    };
  }

  private getScaleDegreeMidiNumbers(scaleDegree: string): number[] {
    const scale = generateMajorScale(this.options.keyRoot); // Get the major scale based on the keyRoot
    let chordNotes: number[] = [];

    switch (scaleDegree) {
      case "I":
        chordNotes = generateChord(scale[0], "major").notes; // Root chord (major)
        break;
      case "ii":
        chordNotes = generateChord(scale[1], "minor").notes; // Supertonic chord (minor)
        break;
      case "iii":
        chordNotes = generateChord(scale[2], "minor").notes; // Mediant chord (minor)
        break;
      case "IV":
        chordNotes = generateChord(scale[3], "major").notes; // Subdominant chord (major)
        break;
      case "V":
        chordNotes = generateChord(scale[4], "major").notes; // Dominant chord (major)
        break;
      case "vi":
        chordNotes = generateChord(scale[5], "minor").notes; // Submediant chord (minor)
        break;
      case "vii°":
        chordNotes = generateChord(scale[6], "diminished").notes; // Leading tone diminished chord
        break;
      default:
        chordNotes = [];
    }

    return chordNotes;
  }

  // Helper function to generate chord inversions
  private getChordInversion(chordNotes: number[], inversion: number): number[] {
    const chordLength = chordNotes.length;

    // Inversions are done by rotating the notes of the chord
    if (inversion === 1) {
      // First inversion (root's 3rd as the lowest note)
      return [...chordNotes.slice(1), chordNotes[0]];
    } else if (inversion === 2) {
      // Second inversion (root's 5th as the lowest note)
      return [...chordNotes.slice(2), chordNotes[0], chordNotes[1]];
    } else {
      return chordNotes; // Root position
    }
  }

  // Helper function to compare two arrays
  private areArraysEqual(arr1: number[], arr2: number[]): boolean {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }

    return true;
  }
}
